import mammoth from 'mammoth'
import * as pdfjsLib from 'pdfjs-dist'
import { createWorker } from 'tesseract.js'

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

export interface ProcessedDocument {
  content: string
  metadata?: {
    wordCount: number
    pageCount?: number
    author?: string
    isImage?: boolean
    imageUrl?: string
  }
}

export class DocumentProcessor {
  static async processFile(file: File): Promise<ProcessedDocument> {
    const extension = file.name.toLowerCase().split('.').pop()
    
    // Check if it's an image file
    if (file.type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension || '')) {
      return await this.processImage(file)
    }
    
    switch (extension) {
      case 'docx':
        return await this.processDocx(file)
      case 'doc':
        return await this.processDoc(file)
      case 'pdf':
        return await this.processPdf(file)
      case 'txt':
      case 'md':
      case 'rtf':
        return await this.processText(file)
      default:
        // Try to process as text for unknown types
        return await this.processText(file)
    }
  }

  private static async processImage(file: File): Promise<ProcessedDocument> {
    try {
      // Create object URL for the image
      const imageUrl = URL.createObjectURL(file)
      
      // Create Tesseract worker
      const worker = await createWorker('eng')
      
      try {
        // Perform OCR on the image
        const { data: { text } } = await worker.recognize(file)
        
        return {
          content: text.trim(),
          metadata: {
            wordCount: text.trim().split(/\s+/).filter(word => word.length > 0).length,
            isImage: true,
            imageUrl: imageUrl
          }
        }
      } finally {
        // Clean up worker
        await worker.terminate()
      }
    } catch (error) {
      console.error('Error processing image file:', error)
      throw new Error('Failed to extract text from image. Please ensure the image contains readable text.')
    }
  }

  private static async processDocx(file: File): Promise<ProcessedDocument> {
    try {
      const arrayBuffer = await file.arrayBuffer()
      const result = await mammoth.extractRawText({ arrayBuffer })
      
      return {
        content: result.value,
        metadata: {
          wordCount: result.value.trim().split(/\s+/).length
        }
      }
    } catch (error) {
      console.error('Error processing DOCX file:', error)
      throw new Error('Failed to process DOCX file')
    }
  }

  private static async processDoc(file: File): Promise<ProcessedDocument> {
    // For .doc files, try to read as text (limited support)
    // In a production app, you might want to use a more robust solution
    try {
      const text = await this.readAsText(file)
      return {
        content: text,
        metadata: {
          wordCount: text.trim().split(/\s+/).length
        }
      }
    } catch (error) {
      throw new Error('Failed to process DOC file. Please convert to DOCX or TXT format.')
    }
  }

  private static async processPdf(file: File): Promise<ProcessedDocument> {
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
      let fullText = ''
      let pageCount = pdf.numPages

      // Extract text from each page
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const textContent = await page.getTextContent()
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ')
        fullText += pageText + '\n\n'
      }

      return {
        content: fullText.trim(),
        metadata: {
          wordCount: fullText.trim().split(/\s+/).length,
          pageCount: pageCount
        }
      }
    } catch (error) {
      console.error('Error processing PDF file:', error)
      throw new Error('Failed to process PDF file. The file may be corrupted or password-protected.')
    }
  }

  private static async processText(file: File): Promise<ProcessedDocument> {
    try {
      const content = await this.readAsText(file)
      return {
        content,
        metadata: {
          wordCount: content.trim().split(/\s+/).length
        }
      }
    } catch (error) {
      throw new Error('Failed to process text file')
    }
  }

  private static readAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      
      reader.onload = (e) => {
        const result = e.target?.result as string
        resolve(result)
      }
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'))
      }

      reader.readAsText(file)
    })
  }

  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }
} 